import { DeploymentStatus } from "@/web/components/deployment-status";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, LogIn } from "lucide-react";
import { useAuth, type Session } from "@/web/auth";
import { api, errorMessage } from "@/web/lib/api";
import { safeNext } from "@/web/lib/next";
import { Button, Input } from "@/web/components/ui";
import { Turnstile, type TurnstileHandle } from "@/web/components/turnstile";

interface SsoConfig {
  enabled: boolean;
  sailixAppUrl: string | null;
  publicSignup: boolean;
}

export function useSsoConfig() {
  const [config, setConfig] = useState<SsoConfig | null>(null);
  useEffect(() => {
    api<SsoConfig>("/sso/config")
      .then(setConfig)
      .catch(() => setConfig({ enabled: false, sailixAppUrl: null, publicSignup: true }));
  }, []);
  return config;
}

/** Top-level navigation into the SSO hop; the chain ends on another origin and comes back. */
export function ssoStartHref(returnTo: string) {
  return `/api/sso/start?return=${encodeURIComponent(returnTo)}`;
}

export function LoginPage() {
  const { session, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const sso = useSsoConfig();
  const destination = safeNext(params.get("next"));
  if (session) return <Navigate to={destination} replace />;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    try {
      await api<Session>("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: form.get("email"),
          password: form.get("password"),
          turnstileToken: form.get("turnstileToken") || undefined,
        }),
      });
      await refresh();
      navigate(destination);
    } catch (reason) {
      setError(errorMessage(reason, "Sign in failed."));
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  }
  const ssoEnabled = sso?.enabled ?? false;
  const passwordVisible = !ssoEnabled || showPassword;
  return (
    <AuthSurface>
      <div className="auth-heading">
        <h1>Sign in to Sailix Support</h1>
        <p>For the Sailix team: the support inbox. For agencies: your tickets with Sailix.</p>
      </div>
      {ssoEnabled && (
        <div className="auth-form">
          <Button asChild className="sso-button">
            <a href={ssoStartHref(destination)}>
              <LogIn size={16} />
              Continue with Sailix
            </a>
          </Button>
          <p className="auth-switch">
            Agency staff:{" "}
            <Link to="/portal/login">open the agency portal</Link>
          </p>
          {!passwordVisible && (
            <button type="button" className="auth-form-link" onClick={() => setShowPassword(true)}>
              Sign in with a password instead
            </button>
          )}
        </div>
      )}
      {passwordVisible && (
        <form onSubmit={submit} className="auth-form">
          {ssoEnabled && <div className="sso-divider">or</div>}
          <label>
            Email
            <Input name="email" type="email" autoComplete="email" required />
          </label>
          <label>
            Password
            <Input name="password" type="password" autoComplete="current-password" minLength={12} required />
          </label>
          <Turnstile ref={turnstileRef} />
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <Button disabled={submitting} type="submit" variant={ssoEnabled ? "secondary" : "primary"}>
            {submitting ? "Signing in…" : "Sign in"}
            <ArrowRight size={16} />
          </Button>
          <Link className="auth-form-link" to="/forgot-password">
            Forgot password?
          </Link>
        </form>
      )}
      {sso?.publicSignup && (
        <p className="auth-switch">
          New workspace? <Link to="/signup">Create an account</Link>
        </p>
      )}
    </AuthSurface>
  );
}

export function AuthSurface({ children }: { children: React.ReactNode }) {
  return (
    <main className="auth-surface">
      <section className="auth-panel">
        <Link className="auth-brand" to="/" aria-label="Sailix Support">
          <img className="sailix-logo" src="/sailix-logo-nav.png" alt="Sailix" width={96} height={32} />
        </Link>
        <DeploymentStatus />
        {children}
        <p className="sailix-attribution">
          Powered by{" "}
          <a href="https://github.com/mirza-rizvi/ResolveHQ" rel="noreferrer">
            ResolveHQ
          </a>
          .
        </p>
      </section>
      <aside className="auth-aside" aria-hidden="true">
        <span>One thread per agency, worked together with the Sailix team.</span>
        <ol>
          <li>Raise a ticket from the Sailix app</li>
          <li>Track replies and attachments in one place</li>
          <li>Resolve, rate, and move on</li>
        </ol>
      </aside>
    </main>
  );
}
